import moment from 'moment';
import { IFilterType } from '../types/ProfileTypes';
import { isArray, isNil, toNumber, toString } from 'lodash';

const columnsWithMultipleStrings = ['pickupSubTerminal', 'dropOffSubTerminal'];

const expresionTime = [
  { expression: new RegExp('^([0-1]?[0-9]|2[0-3]):[0-5][0-9] [APap][nM]'), format: 'hh:mm A' },
  { expression: new RegExp('^([0-1]?[0-9]|2[0-3]):[0-5][0-9]'), format: 'HH:mm' }
];

const isSubTerminalFilter = (value: string, filter: IFilterType) => {
  return (
    filter.field === 'startLocationType' &&
    !['HOME', 'TERMINAL', 'SLEEPER'].includes(value) &&
    filter.value === '/hierarchy/subTerminal'
  );
};

const applyInFilter = (value: string[], filter: string) => {
  return value.includes(filter);
};

const applyNotInFilter = (value: string[], filter: string) => {
  return !value.includes(filter);
};

const applyEqualityFilter = (value: string, filter: IFilterType) => {
  return String(value).toLowerCase() === String(filter.value).toLowerCase() || isSubTerminalFilter(value, filter);
};

const applyContainFilter = (value: string, filter: string) => {
  return String(value).toLowerCase().includes(String(filter).toLowerCase());
};

const applyGTFilter = (value: string, filter: string) => {
  return toNumber(value) > toNumber(filter);
};

const applyGETFilter = (value: string, filter: string) => {
  return toNumber(value) >= toNumber(filter);
};

const applyLTFilter = (value: string, filter: string) => {
  return toNumber(value) < toNumber(filter);
};

const applyLETFilter = (value: string, filter: string) => {
  return toNumber(value) <= toNumber(filter);
};

const applyStartWithFilter = (value: string, filter: string) => {
  return toString(value).toLowerCase().startsWith(toString(filter).toLowerCase());
};

const applyEndWithFilter = (value: string, filter: string) => {
  return toString(value).toLowerCase().endsWith(toString(filter).toLowerCase());
};

const applyDateGTFilter = (value: string, filter: string, format?: string) => {
  return moment(...convertDateToFormat(filter, format)) < moment(...convertDateToFormat(value, format));
};

const applyDateGETFilter = (value: string, filter: string, format?: string) => {
  return moment(...convertDateToFormat(filter, format)) <= moment(...convertDateToFormat(value, format));
};

const applyDateLTFilter = (value: string, filter: string, format?: string) => {
  return moment(...convertDateToFormat(filter, format)) > moment(...convertDateToFormat(value, format));
};

const applyDateLETFilter = (value: string, filter: string, format?: string) => {
  return moment(...convertDateToFormat(filter, format)) >= moment(...convertDateToFormat(value, format));
};

const applyDateEqualFilter = (value: string, filter: string, format?: string) => {
  return moment(...convertDateToFormat(filter, format)).isSame(moment(...convertDateToFormat(value, format)));
};

const convertDateToFormat = (inp: string, format?: string) => {
  return format
    ? !expresionTime.some(item => item.expression.test(inp))
      ? [moment(inp).format(format), format]
      : [inp, format]
    : [inp];
};

const applyArrayInFilter = (filter: IFilterType, value: string) => {
  if (columnsWithMultipleStrings.includes(filter.field)) {
    return applyArrayInFilterWhenValueContainsMultipleElements(filter.value, value);
  }
  return applyArrayInFilterWhenValueContainsOneElement(filter.value, value);
};

const applyArrayInFilterWhenValueContainsOneElement = (list: string[], value: string) => {
  let aux = false;
  if (isArray(list)) {
    for (const item of list) {
      if (value.toString() === item.toString()) {
        aux = true;
        break;
      }
    }
  }
  return aux;
};

const applyArrayInFilterWhenValueContainsMultipleElements = (list: string[], value: string) => {
  const elements = value.split(', ');
  let aux = false;
  if (isArray(list)) {
    for (const item of list) {
      for (const element of elements) {
        if (element.trim().toString() === item.toString()) {
          aux = true;
          break;
        }
      }
    }
  }
  return aux;
};

const applyFilters = (filter: IFilterType, value: any) => {
  let format: string | undefined = undefined;
  expresionTime.some((item: any) => {
    const result = item.expression.test(value);
    if (result) {
      format = item.format;
    }
    return result;
  });

  switch (filter.operator) {
    case 'CONTAINS':
      return applyContainFilter(value, filter.value);
    case 'EQUALITY':
      return applyEqualityFilter(value, filter);
    case 'DATE_LESS_EQUAL_THAN':
      return applyDateLETFilter(getDateTimeValue(value), filter.value, format);
    case 'DATE_LESS_THAN':
      return applyDateLTFilter(getDateTimeValue(value), filter.value, format);
    case 'DATE_GREATER_EQUAL_THAN':
      return applyDateGETFilter(getDateTimeValue(value), filter.value, format);
    case 'DATE_GREATER_THAN':
      return applyDateGTFilter(getDateTimeValue(value), filter.value, format);
    case 'DATE_EQUAL':
      return applyDateEqualFilter(getDateTimeValue(value), filter.value, format);
    case 'ENDS_WITH':
      return applyEndWithFilter(value, filter.value);
    case 'STARTS_WITH':
      return applyStartWithFilter(value, filter.value);
    case 'GREATER_THAN':
      return applyGTFilter(value, filter.value);
    case 'GREATER_EQUALS_THAN':
    case 'NUMBER_GREATER_EQUAL_THAN':
      return applyGETFilter(value, filter.value);
    case 'LESS_THAN':
      return applyLTFilter(value, filter.value);
    case 'LESS_EQUALS_THAN':
    case 'NUMBER_LESS_EQUAL_THAN':
      return applyLETFilter(value, filter.value);
    case 'IN':
      return applyArrayInFilter(filter, value);
    case 'NOT_IN':
      return !applyArrayInFilter(filter, value);
  }
};

const iterateBySearch = (data: any, search: string) => {
  let exist = false;
  for (const d in data) {
    if (data.hasOwnProperty(d) && !isNil(data[d]) && data[d].toString().toLowerCase().includes(search.toLowerCase())) {
      exist = true;
      break;
    }
  }
  return exist;
};

const getDateTimeValue = (value: any) => {
  return value?.date && value?.time ? `${value.date} ${value.time}` : value;
};

export const PaginationUtils = {
  applyInFilter,
  applyNotInFilter,
  applyEqualityFilter,
  applyContainFilter,
  applyGTFilter,
  applyGETFilter,
  applyLTFilter,
  applyLETFilter,
  applyStartWithFilter,
  applyEndWithFilter,
  applyDateGTFilter,
  applyDateGETFilter,
  applyDateLTFilter,
  applyDateLETFilter,
  applyDateEqualFilter,
  applyArrayInFilter,
  applyFilters,
  iterateBySearch
};
